/**
 * `AssessmentWizard` — flusso guidato di autovalutazione.
 *
 * Design ref: sezione Frontend → `AssessmentWizard`: "il voto informativo
 * diventa 'Propensione' (facoltativa); Livello obbligatorio, Esperienza
 * facoltativa".
 *
 *  - selezione di una `Competenza`;
 *  - presentazione di **tutte** le skill della competenza scelta in un'unica
 *    schermata, una sotto l'altra;
 *  - il **Livello_Self** (`LivelloSelect`) è l'unico campo obbligatorio: il
 *    salvataggio dell'autovalutazione resta bloccato finché non è scelto
 *    (Req. 8.6);
 *  - la fascia di **Esperienza** (`ExperienceRangeSelect`) è facoltativa;
 *  - il selettore `PropensionSelect` rappresenta la **Propensione** (voto
 *    informativo di volontà di approfondire, interesse a crescere): dato
 *    puramente informativo e facoltativo, separato visivamente dai valori che
 *    concorrono al punteggio (Req. 8.1, 8.2, 1.1);
 *  - salvataggio via `PUT /api/assessments/me/:skillId/self`; i voti sono
 *    sempre modificabili.
 *
 * La Propensione distingue lo stato `null` (nessuna preferenza espressa) dai
 * valori `0..10`; `null` è distinto da `0` (Req. 1.6). Il campo dati resta
 * `selfInterest`: "Propensione" è solo copy rivolto all'utente.
 *
 * Lo stato server (competenze, skill, assessment) è gestito con TanStack Query;
 * il salvataggio invalida la query degli assessment per riflettere lo stato
 * corrente (overwrite, nessuno storico).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssessmentView,
  CompetenceDto,
  DepartmentDto,
  ExperienceRange,
  SetScoreRequest,
  SkillDto,
} from '../shared/dto';
import {
  DEFAULT_SKILL_WEIGHT,
  LIVELLO_VALUES,
  livelloRank,
  type Livello,
} from '../shared/dto';
import { useApiClient } from '../api/useApiClient';
import { useAuth } from '../auth/AuthProvider';
import { ExperienceRangeSelect } from '../components/ExperienceRangeSelect';
import { LivelloGuide } from '../components/LivelloGuide';
import { LivelloSelect } from '../components/LivelloSelect';
import { PropensionSelect } from '../components/PropensionSelect';
import { CompetenceInfoPanel } from '../components/CompetenceInfoPanel';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Icon,
  Loading,
  PageHeader,
  TextInput,
} from '../components/ui';

/**
 * Valore del filtro reparto: `'all'` (tutti i reparti) oppure l'`id` di uno
 * specifico reparto.
 */
export type DepartmentFilter = 'all' | string;

/**
 * Filtra le competenze per reparto e per una query testuale (case-insensitive),
 * confrontando `name` e `description`.
 *
 * - `departmentFilter === 'all'`: nessun filtro sul reparto.
 * - `departmentFilter === <id>`: mantiene solo le competenze del reparto scelto
 *   (`departmentId === <id>`).
 *
 * Il filtro testuale è applicato dopo quello del reparto: la ricerca opera
 * quindi all'interno del reparto selezionato. Con query vuota e reparto `'all'`
 * ritorna l'elenco intero.
 *
 * Il risultato è sempre ordinato alfabeticamente per `name` (case‑insensitive,
 * locale `it`).
 */
export function filterCompetences(
  competences: CompetenceDto[],
  query: string,
  departmentFilter: DepartmentFilter = 'all',
): CompetenceDto[] {
  const byDepartment =
    departmentFilter === 'all'
      ? competences
      : competences.filter((c) => c.departmentId === departmentFilter);

  const q = query.trim().toLowerCase();
  const matched =
    q.length === 0
      ? byDepartment
      : byDepartment.filter((c) => {
          const haystack = `${c.name} ${c.description ?? ''}`.toLowerCase();
          return haystack.includes(q);
        });

  return [...matched].sort((a, b) =>
    a.name.localeCompare(b.name, 'it', { sensitivity: 'base' }),
  );
}

/**
 * Percentuale di completamento dell'autovalutazione di una competenza: quota di
 * skill con il **Livello_Self** registrato (non `null`) sul totale delle skill
 * della competenza. Il Livello è l'unico campo obbligatorio e quindi il segnale
 * che indica una skill effettivamente autovalutata (Req. 8.6). Ritorna un
 * intero `0..100`; `0` se la competenza non ha skill.
 *
 * Il Livello "Nessuno" (`e-0`) è un valore **scelto** dall'utente, non
 * l'assenza di scelta: una skill con Livello_Self `e-0` è a tutti gli effetti
 * autovalutata (la persona dichiara di non possedere la competenza) e conta
 * come completata. Solo l'assenza di Livello (`null`, mai salvato) resta
 * "non completata".
 *
 * La firma resta generica su `Map<string, T | null>` e conta i valori non
 * `null`: il chiamante passa una mappa `skillId → Livello_Self`.
 */
export function completionPercent<T>(
  competence: Pick<CompetenceDto, 'skillIds'>,
  completedBySkill: Map<string, T | null>,
): number {
  const total = competence.skillIds.length;
  if (total === 0) return 0;
  const done = competence.skillIds.reduce((count, skillId) => {
    const value = completedBySkill.get(skillId);
    return count + (value !== undefined && value !== null ? 1 : 0);
  }, 0);
  return Math.round((done / total) * 100);
}

/**
 * Stima il "Livello posseduto" sulla competenza a partire dai Livelli_Self
 * correnti delle sue skill: media **pesata** dei rank dei soli Livelli valutati
 * (le skill senza Self non diluiscono), arrotondata al {@link Livello} discreto
 * (`e-0..e-5`). Restituisce `null` se nessuna skill della competenza ha un
 * Livello_Self, così la card del Livello posseduto resta senza evidenziazione.
 *
 * A differenza del dettaglio competenza — che deriva il Livello dalla
 * Proficiency della dashboard — qui si usano i Livelli_Self in corso di
 * modifica (incluse le bozze), così l'evidenziazione si aggiorna live durante la
 * valutazione.
 */
function ownedLevelFromSelfLevels(
  competence: Pick<CompetenceDto, 'skillIds' | 'skillWeights'>,
  livelloOf: (skillId: string) => Livello | null,
): Livello | null {
  const weights = competence.skillWeights ?? {};
  let weightedSum = 0;
  let weightTotal = 0;
  for (const skillId of competence.skillIds) {
    const livello = livelloOf(skillId);
    if (livello === null) continue;
    const weight = weights[skillId] ?? DEFAULT_SKILL_WEIGHT;
    weightedSum += livelloRank(livello) * weight;
    weightTotal += weight;
  }
  if (weightTotal === 0) return null;
  const avgRank = Math.round(weightedSum / weightTotal);
  return LIVELLO_VALUES[Math.min(LIVELLO_VALUES.length - 1, Math.max(0, avgRank))];
}

/** Query keys locali del wizard. */
const QK = {
  competences: ['competences'] as const,
  skills: ['skills'] as const,
  departments: ['departments'] as const,
  assessmentsMe: ['assessments', 'me'] as const,
};

export default function AssessmentWizard() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { me } = useAuth();

  // Competenza pre-selezionata via query string (`/assessment?competenceId=…`),
  // usata quando si arriva dal "Dettaglio competenza".
  const [searchParams] = useSearchParams();
  const initialCompetenceId = searchParams.get('competenceId');

  // Competenza selezionata (6.1). Se il link porta un `competenceId`, il wizard
  // parte già sulla schermata di valutazione di quella competenza.
  const [competenceId, setCompetenceId] = useState<string | null>(
    initialCompetenceId,
  );

  // Bozza locale del Livello_Self per skill, in fase di autovalutazione. Il
  // salvataggio richiede il Livello (Req. 8.6): finché non è scelto, il
  // salvataggio resta bloccato. La bozza si azzera dopo un salvataggio riuscito,
  // così la riga torna a riflettere i dati server.
  const [livelloDraft, setLivelloDraft] = useState<Record<string, Livello>>({});
  // Bozza della Propensione (voto informativo) per skill: dato **opzionale** e
  // informativo (Req. 1.1, 8.1, 8.2), non è condizione di salvataggio. Viaggia
  // nello stesso PUT del self quando il Livello è presente.
  const [interestDraft, setInterestDraft] = useState<Record<string, number>>({});
  // Bozza degli anni di esperienza per skill: dato **opzionale** e informativo,
  // non è condizione di salvataggio. Viaggia nello stesso PUT del self quando il
  // Livello è presente.
  const [yearsDraft, setYearsDraft] = useState<Record<string, ExperienceRange>>({});
  // Ref allineati alle bozze: permettono agli handler di leggere il valore più
  // recente degli *altri* campi senza dipendere da uno stato "stale" del
  // closure, così il PUT trasporta sempre l'interesse/gli anni correnti insieme
  // al Livello, in qualsiasi ordine siano stati scelti.
  const interestDraftRef = useRef<Record<string, number>>({});
  const livelloDraftRef = useRef<Record<string, Livello>>({});
  const yearsDraftRef = useRef<Record<string, ExperienceRange>>({});

  // Testo di ricerca per filtrare la griglia di competenze (primo step di
  // selezione).
  const [search, setSearch] = useState('');

  // Filtro per reparto integrato nel campo di ricerca. Di default punta al
  // reparto dell'utente; `'all'` mostra le competenze di tutti i reparti.
  // `null` = non ancora inizializzato dal profilo (`/api/me`).
  const [departmentFilter, setDepartmentFilter] = useState<DepartmentFilter | null>(
    null,
  );

  // Inizializza il filtro sul reparto dell'utente appena il profilo è noto.
  // Se l'utente non ha reparto assegnato ripiega su `'all'` (Tutti).
  useEffect(() => {
    if (departmentFilter !== null) return;
    if (me === null) return;
    setDepartmentFilter(me.departmentId ?? 'all');
  }, [me, departmentFilter]);

  // Filtro effettivo usato dal rendering finché il profilo non è risolto.
  const effectiveDepartmentFilter: DepartmentFilter = departmentFilter ?? 'all';

  // Elenco delle competenze selezionabili (6.1).
  const competencesQuery = useQuery({
    queryKey: QK.competences,
    queryFn: () => api.get<CompetenceDto[]>('/api/competences'),
  });

  // Elenco dei reparti per popolare il filtro integrato nella ricerca.
  const departmentsQuery = useQuery({
    queryKey: QK.departments,
    queryFn: () => api.get<DepartmentDto[]>('/api/departments'),
  });

  // Catalogo skill: serve per risolvere i nomi delle skill della competenza.
  const skillsQuery = useQuery({
    queryKey: QK.skills,
    queryFn: () => api.get<SkillDto[]>('/api/skills'),
  });

  // Assessment correnti dell'utente: mostrano Livello_Self, esperienza e
  // Propensione già registrati, così i voti restano sempre modificabili.
  const assessmentsQuery = useQuery({
    queryKey: QK.assessmentsMe,
    queryFn: () => api.get<AssessmentView[]>('/api/assessments/me'),
  });

  const competences = competencesQuery.data ?? [];
  const departments = departmentsQuery.data ?? [];
  const selectedCompetence = competences.find((c) => c.id === competenceId) ?? null;

  // Competenze filtrate per reparto e testo di ricerca (griglia del primo step).
  const filteredCompetences = useMemo(
    () => filterCompetences(competences, search, effectiveDepartmentFilter),
    [competences, search, effectiveDepartmentFilter],
  );

  // Skill della competenza selezionata, nell'ordine di `skillIds` (6.2).
  const skillsToRate: SkillDto[] = useMemo(() => {
    if (!selectedCompetence) return [];
    const byId = new Map((skillsQuery.data ?? []).map((s) => [s.id, s]));
    return selectedCompetence.skillIds
      .map((skillId) => byId.get(skillId))
      .filter((s): s is SkillDto => s !== undefined);
  }, [selectedCompetence, skillsQuery.data]);

  // Mappa della Propensione corrente per skill (voto informativo), per
  // pre‑popolare il `PropensionSelect`. Assente ⇒ `null`.
  const interestBySkill = useMemo(() => {
    const map = new Map<string, number | null>();
    for (const a of assessmentsQuery.data ?? []) {
      map.set(a.skillId, a.selfInterest);
    }
    return map;
  }, [assessmentsQuery.data]);

  // Mappa del Livello_Self corrente per skill (per pre‑popolare `LivelloSelect`).
  const livelloBySkill = useMemo(() => {
    const map = new Map<string, Livello | null>();
    for (const a of assessmentsQuery.data ?? []) {
      map.set(a.skillId, a.livelloSelf);
    }
    return map;
  }, [assessmentsQuery.data]);

  // Mappa degli anni di esperienza correnti per skill (per pre‑popolare
  // `ExperienceRangeSelect`). Dato opzionale: assente ⇒ `null`.
  const yearsBySkill = useMemo(() => {
    const map = new Map<string, ExperienceRange | null>();
    for (const a of assessmentsQuery.data ?? []) {
      map.set(a.skillId, a.yearsOfExperience);
    }
    return map;
  }, [assessmentsQuery.data]);

  // Salvataggio dell'autovalutazione: PUT /api/assessments/me/:skillId/self. Il
  // Livello_Self è l'unico campo obbligatorio nel body (Req. 8.6); la Propensione
  // (interesse) e gli anni di esperienza sono opzionali e informativi (Req. 8.1,
  // 8.2, 1.1). La Propensione può essere `null` (nessuna preferenza).
  const saveSelfScore = useMutation({
    mutationFn: async ({
      skillId,
      livello,
      interest,
      yearsOfExperience,
    }: {
      skillId: string;
      livello: Livello;
      interest: number | null;
      yearsOfExperience: ExperienceRange | null;
    }) => {
      const body: SetScoreRequest = {
        livello,
        interest: interest ?? null,
        yearsOfExperience,
      };
      await api.put<void>(`/api/assessments/me/${encodeURIComponent(skillId)}/self`, body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QK.assessmentsMe });
    },
  });

  const handleSelectCompetence = (id: string) => {
    setCompetenceId(id === '' ? null : id);
  };

  // Torna alla griglia di selezione delle competenze (primo step).
  const backToSelection = () => {
    setCompetenceId(null);
  };

  // Skill attualmente in fase di salvataggio: consente di disabilitare/mostrare
  // lo stato di caricamento solo sulla riga interessata, non su tutta la lista.
  const savingSkillId = saveSelfScore.isPending
    ? saveSelfScore.variables?.skillId ?? null
    : null;

  // Livello correntemente **visualizzato** per una skill: bozza locale se
  // presente, altrimenti il Livello salvato sul server. Per una skill mai
  // valutata (nessuna bozza, nessun valore server) resta `null`: il
  // `LivelloSelect` mostra il placeholder "non selezionato" e la skill NON conta
  // come completata. Il Livello "Nessuno" (`e-0`) è una scelta **esplicita**
  // dell'utente, distinta dall'assenza di scelta.
  const currentLivello = (skillId: string): Livello | null =>
    livelloDraft[skillId] ?? livelloBySkill.get(skillId) ?? null;

  // Propensione correntemente scelta per una skill (dato opzionale): bozza
  // locale se presente, altrimenti il valore salvato sul server.
  const currentInterest = (skillId: string): number | null =>
    interestDraft[skillId] ?? interestBySkill.get(skillId) ?? null;

  // Anni di esperienza correnti per una skill: bozza locale se presente,
  // altrimenti il valore salvato sul server (dato opzionale).
  const currentYears = (skillId: string): ExperienceRange | null =>
    yearsDraft[skillId] ?? yearsBySkill.get(skillId) ?? null;

  // Varianti "robuste" che leggono la bozza dal ref (valore più recente) e
  // ripiegano sul server: usate dagli handler al momento del salvataggio.
  const latestLivello = (skillId: string): Livello | null =>
    livelloDraftRef.current[skillId] ?? livelloBySkill.get(skillId) ?? null;
  const latestInterest = (skillId: string): number | null =>
    interestDraftRef.current[skillId] ?? interestBySkill.get(skillId) ?? null;
  const latestYears = (skillId: string): ExperienceRange | null =>
    yearsDraftRef.current[skillId] ?? yearsBySkill.get(skillId) ?? null;

  // Salva l'autovalutazione di una skill: richiede il Livello (Req. 8.6);
  // interesse e anni sono opzionali e viaggiano nello stesso PUT. I voti restano
  // modificabili. Al successo azzera la bozza della riga così da riflettere i
  // dati server aggiornati.
  const persist = (
    skillId: string,
    livello: Livello,
    interest: number | null,
    yearsOfExperience: ExperienceRange | null,
  ) => {
    saveSelfScore.mutate(
      { skillId, livello, interest, yearsOfExperience },
      {
        onSuccess: () => {
          delete livelloDraftRef.current[skillId];
          delete interestDraftRef.current[skillId];
          delete yearsDraftRef.current[skillId];
          setLivelloDraft(({ [skillId]: _livello, ...rest }) => rest);
          setInterestDraft(({ [skillId]: _interest, ...rest }) => rest);
          setYearsDraft(({ [skillId]: _years, ...rest }) => rest);
        },
      },
    );
  };

  // Cambio del Livello (campo obbligatorio): aggiorna la bozza e salva subito
  // con l'interesse/gli anni correnti (letti dai ref), dato che il Livello è
  // l'unica condizione richiesta per il salvataggio (Req. 8.6).
  //
  // Livello "Nessuno" (`e-0`): la risorsa non possiede la competenza, quindi gli
  // anni di esperienza non hanno significato. Azzeriamo la bozza degli anni
  // (ref + stato) e persistiamo con `yearsOfExperience: null`; il select
  // Esperienza è anche disabilitato lato render finché il Livello resta `e-0`.
  const handleLivelloChange = (skillId: string, livello: Livello) => {
    livelloDraftRef.current = { ...livelloDraftRef.current, [skillId]: livello };
    setLivelloDraft((prev) => ({ ...prev, [skillId]: livello }));

    if (livello === 'e-0') {
      delete yearsDraftRef.current[skillId];
      setYearsDraft(({ [skillId]: _years, ...rest }) => rest);
      persist(skillId, livello, latestInterest(skillId), null);
      return;
    }

    persist(skillId, livello, latestInterest(skillId), latestYears(skillId));
  };

  // Cambio della Propensione (dato opzionale): aggiorna la bozza dell'interesse
  // e ripersiste **solo** se il Livello è già impostato; altrimenti l'interesse
  // resta in bozza e verrà incluso quando si sceglierà il Livello (la
  // Propensione da sola non salva — il Livello è obbligatorio). `null` azzera la
  // bozza (nessuna preferenza), rispecchiando la gestione di `handleYearsChange`.
  const handleInterestChange = (skillId: string, interest: number | null) => {
    if (interest === null) {
      delete interestDraftRef.current[skillId];
      setInterestDraft(({ [skillId]: _interest, ...rest }) => rest);
    } else {
      interestDraftRef.current = { ...interestDraftRef.current, [skillId]: interest };
      setInterestDraft((prev) => ({ ...prev, [skillId]: interest }));
    }
    const livello = latestLivello(skillId);
    if (livello !== null) persist(skillId, livello, interest, latestYears(skillId));
  };

  // Cambio degli anni di esperienza (dato opzionale): aggiorna la bozza e, se il
  // Livello è già presente, ripersiste per riflettere subito il dato; altrimenti
  // resta in bozza e verrà incluso quando si sceglierà il Livello.
  const handleYearsChange = (
    skillId: string,
    years: ExperienceRange | null,
  ) => {
    if (years === null) {
      delete yearsDraftRef.current[skillId];
      setYearsDraft(({ [skillId]: _years, ...rest }) => rest);
    } else {
      yearsDraftRef.current = { ...yearsDraftRef.current, [skillId]: years };
      setYearsDraft((prev) => ({ ...prev, [skillId]: years }));
    }
    const livello = latestLivello(skillId);
    if (livello !== null) {
      persist(skillId, livello, latestInterest(skillId), years);
    }
  };

  // ----- Rendering -----

  if (competencesQuery.isLoading) {
    return <Loading>Caricamento delle competenze…</Loading>;
  }

  if (competencesQuery.isError) {
    return (
      <section role="alert">
        <h1>Competenze</h1>
        <p>Impossibile caricare le competenze. Riprova più tardi.</p>
      </section>
    );
  }

  return (
    <section className="assessment-wizard" aria-labelledby="assessment-wizard-title">
      <PageHeader
        title="Competenze"
        titleId="assessment-wizard-title"
        subtitle="Valuta le tue skill competenza per competenza."
        actions={
          selectedCompetence !== null ? (
            <Button type="button" variant="ghost" onClick={backToSelection}>
              ← Torna alle competenze
            </Button>
          ) : undefined
        }
      />

      {/* Primo step: selezione della competenza tramite griglia + ricerca (6.1). */}
      {selectedCompetence === null && (
        <div className="competence-picker">
          <Field
            id="competence-search"
            label="Cerca competenza"
            hint="Filtra per business unit, nome o descrizione. La ricerca opera nella business unit selezionata."
          >
            <div className="competence-search">
              <span className="competence-search__department user-admin__select">
                <select
                  id="competence-search-department"
                  aria-label="Filtra per business unit"
                  value={effectiveDepartmentFilter}
                  disabled={departmentsQuery.isLoading}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                >
                  <option value="all">Tutte le business unit</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </span>
              <TextInput
                id="competence-search"
                className="competence-search__input"
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca una competenza…"
                autoComplete="off"
              />
            </div>
          </Field>

          {filteredCompetences.length === 0 ? (
            <EmptyState role="status">
              {competences.length === 0
                ? 'Non ci sono competenze da valutare.'
                : 'Nessuna competenza corrisponde alla business unit o alla ricerca.'}
            </EmptyState>
          ) : (
            <ul className="competence-grid" aria-label="Competenze disponibili">
              {filteredCompetences.map((c) => (
                <li key={c.id}>
                  <Card
                    interactive
                    className="competence-grid__card"
                    role="button"
                    tabIndex={0}
                    aria-label={`Seleziona la competenza ${c.name}`}
                    onClick={() => handleSelectCompetence(c.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSelectCompetence(c.id);
                      }
                    }}
                  >
                    <h3 className="competence-grid__title">{c.name}</h3>
                    {c.description && (
                      <p className="competence-grid__desc">{c.description}</p>
                    )}
                    <p className="competence-grid__meta">
                      {c.skillIds.length === 1
                        ? '1 skill'
                        : `${c.skillIds.length} skill`}
                    </p>
                    {(() => {
                      const pct = completionPercent(c, livelloBySkill);
                      return (
                        <div
                          className="competence-grid__completion"
                          role="progressbar"
                          aria-valuenow={pct}
                          aria-valuemin={0}
                          aria-valuemax={100}
                          aria-label={`Completamento competenza ${pct}%`}
                        >
                          <span className="competence-grid__completion-bar">
                            <span
                              className="competence-grid__completion-fill"
                              style={{ width: `${pct}%` }}
                            />
                          </span>
                          <span className="competence-grid__completion-label">
                            {pct}% completato
                          </span>
                          {/* Assessment completo: spunta verde in un cerchio,
                              segnale immediato che la competenza è al 100%. */}
                          {pct === 100 && (
                            <span
                              className="competence-grid__completion-check"
                              aria-label="Competenza completata"
                              title="Competenza completata"
                            >
                              <Icon name="check" size={14} />
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </Card>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Competenza selezionata: stesso layout del dettaglio competenza — card
          hero (con descrizione e mission) + 5 card dei Livelli e-CF. Il donut
          mostra il completamento dell'autovalutazione (quota di skill con
          Livello_Self). Il ritorno alla selezione è l'azione "Torna alle
          competenze" nell'intestazione in alto a destra. */}
      {selectedCompetence !== null && (
        <CompetenceInfoPanel
          competence={selectedCompetence}
          badge="Competenza in valutazione"
          donutValue={completionPercent(selectedCompetence, livelloBySkill)}
          donutLabel="Completamento"
          ownedLevel={ownedLevelFromSelfLevels(selectedCompetence, currentLivello)}
        />
      )}

      {selectedCompetence !== null && skillsToRate.length === 0 && (
        <p role="status">La competenza selezionata non ha skill da valutare.</p>
      )}

      {saveSelfScore.isError && (
        <Alert tone="error">Salvataggio non riuscito. Riprova.</Alert>
      )}

      {/* Tutte le skill della competenza in un'unica card (6.2). */}
      {selectedCompetence !== null && skillsToRate.length > 0 && (
        <Card className="assessment-wizard__skills-card">
          <div className="assessment-wizard__skills-header">
            <h3 className="assessment-wizard__skills-title">Skills</h3>
            {/* Guida ai Livelli: una sola volta accanto al titolo, non per ogni
                skill. */}
            <LivelloGuide />
          </div>
          <ul className="assessment-wizard__skills">
            {skillsToRate.map((skill) => {
              // Le skill legacy prive del flag sono trattate come autovalutabili.
              const selfAssessable = skill.selfAssessable ?? true;
              const isSaving = savingSkillId === skill.id;
              const livello = currentLivello(skill.id);
              const interest = currentInterest(skill.id);
              // Con Livello "Nessuno" (e-0) gli anni di esperienza non hanno
              // significato: il valore mostrato è forzato a vuoto (il dato
              // persistito è già azzerato in handleLivelloChange) e il select è
              // disabilitato.
              const yearsValue = livello === 'e-0' ? null : currentYears(skill.id);
              // Il salvataggio richiede il Livello (Req. 8.6): finché manca, il
              // select è bordato di rosso (nessun errore sull'intera riga).
              const needsLevel = livello === null;
              const levelClasses = [
                'user-admin__select',
                'skill-list__picker-level',
                needsLevel ? 'skill-list__picker-level--invalid' : '',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <li key={skill.id} className="assessment-wizard__step">
                  <span className="assessment-wizard__skill-label assessment-wizard__skill-label--stacked">
                    <span className="assessment-wizard__skill-name assessment-wizard__skill-name--sm">
                      {skill.name}
                    </span>
                    {skill.description && (
                      <span className="assessment-wizard__skill-desc">
                        {skill.description}
                      </span>
                    )}
                  </span>
                  {/* Il wizard di assessment è esclusivamente manuale: nessun
                      voto AI né tasto AIgor è mostrato qui. Se la skill non è
                      autovalutabile, al posto degli input mostriamo una nota. */}
                  {selfAssessable ? (
                    <div className="assessment-wizard__inputs">
                      {/* Input che concorrono alla valutazione: Livello
                          (obbligatorio, Req. 8.6) ed Esperienza (facoltativa).
                          Il salvataggio avviene al cambio di un campo, non appena
                          il Livello è scelto (nessun tasto Salva). */}
                      <span
                        className={levelClasses}
                        data-empty={livello === null || undefined}
                      >
                        <LivelloSelect
                          label={`Livello per ${skill.name}`}
                          value={livello}
                          disabled={isSaving}
                          onChange={(next) => handleLivelloChange(skill.id, next)}
                        />
                      </span>
                      {/* Anni di esperienza: dato opzionale e informativo.
                          Con Livello "Nessuno" (e-0) la competenza è assente:
                          il select è disabilitato e mostrato vuoto (il valore
                          persistito è azzerato in handleLivelloChange). */}
                      <span
                        className="user-admin__select skill-list__picker-years"
                        data-empty={yearsValue === null || undefined}
                      >
                        <ExperienceRangeSelect
                          label={`Anni di esperienza per ${skill.name}`}
                          value={yearsValue}
                          disabled={isSaving || livello === 'e-0'}
                          onChange={(next) => handleYearsChange(skill.id, next)}
                        />
                      </span>
                      {/* Propensione: voto puramente informativo (Req. 1.1,
                          8.1, 8.2), separato visivamente dagli input che
                          concorrono al punteggio. Facoltativo. */}
                      <span className="assessment-wizard__interest">
                        <span
                          className="user-admin__select skill-list__picker-propension"
                          data-empty={interest === null || undefined}
                        >
                          <PropensionSelect
                            label={`Propensione per ${skill.name}`}
                            value={interest}
                            disabled={isSaving}
                            onChange={(v) => handleInterestChange(skill.id, v)}
                          />
                        </span>
                      </span>
                    </div>
                  ) : (
                    <span className="assessment-wizard__not-self-assessable">
                      Questa skill non è autovalutabile.
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </section>
  );
}
